import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { can } from '../../utils/roleUtils';
import WalletTopUpDialog from './WalletTopUpDialog';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  AddCard as TopUpIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import WalletService from '../../services/wallet.service';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { ProTableStats } from '../../components/pro-table';

// One-row 32px toolbar, like the other lists.
const TOOLBAR_SX = {
  '& .MuiInputBase-root': { height: 32 },
  '& .MuiInputBase-input, & .MuiSelect-select': { fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
};

// Only the TransactionType values something actually writes to a wallet ledger.
const TRANSACTION_TYPES = [
  { value: 'ALL', label: 'All Types' },
  { value: 'TOPUP', label: 'Top-Up' },
  { value: 'INVOICE_PAYMENT', label: 'Invoice Payment' },
  { value: 'REFUND', label: 'Refund' },
  { value: 'CUSTOMER_REFUND', label: 'Refund to Customer' },
  { value: 'CHEQUE_TOPUP', label: 'Cheque Top-Up' },
  { value: 'CREDIT_NOTE', label: 'Credit Note' },
  { value: 'ADJUSTMENT', label: 'Adjustment' },
];

const TRANSACTION_STATUSES = [
  { value: 'ALL', label: 'All Statuses' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const TAB_DIRECTIONS = [null, 'CREDIT', 'DEBIT'];

const WalletDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wallet, setWallet] = useState(null);

  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [txLoading, setTxLoading] = useState(false);
  const [txError, setTxError] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [tabValue, setTabValue] = useState(0);
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  // TC-entered top-up (finance write): pending until approved on Wallets → Top-ups.
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [topUpNotice, setTopUpNotice] = useState(null);
  const sessionUser = (() => {
    try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch (e) { return {}; }
  })();
  const canWriteFin = can(sessionUser, 'finance', 'write');

  const fetchWallet = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const walletData = await WalletService.getCustomerWallet(id);
      if (!walletData) {
        setError('Wallet not found');
      } else {
        setWallet(walletData);
      }
    } catch (err) {
      setError('Failed to load wallet details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchSummary = useCallback(async () => {
    try {
      setSummary(await WalletService.getWalletTransactionSummary(id));
    } catch (err) {
      setSummary(null);
    }
  }, [id]);

  const fetchTransactions = useCallback(async () => {
    setTxLoading(true);
    setTxError(null);
    try {
      const params = { page, size: rowsPerPage };
      const direction = TAB_DIRECTIONS[tabValue];
      if (direction) params.direction = direction;
      if (typeFilter !== 'ALL') params.type = typeFilter;
      if (statusFilter !== 'ALL') params.status = statusFilter;

      const result = await WalletService.getWalletTransactions(id, params);
      setTransactions(result.data || []);
      setTotalTransactions(result.total || 0);
    } catch (err) {
      setTransactions([]);
      setTotalTransactions(0);
      setTxError('Failed to load transactions');
    } finally {
      setTxLoading(false);
    }
  }, [id, page, rowsPerPage, tabValue, typeFilter, statusFilter]);

  useEffect(() => { fetchWallet(); }, [fetchWallet]);
  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleTabChange = (e, newVal) => {
    setTabValue(newVal);
    setPage(0);
  };

  const handleTypeFilterChange = (e) => {
    setTypeFilter(e.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/wallets?tab=customers')} sx={{ textTransform: 'none' }}>Back to Wallets</Button>
      </Box>
    );
  }

  if (!wallet) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>Wallet not found</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/wallets?tab=customers')} sx={{ textTransform: 'none' }}>Back to Wallets</Button>
      </Box>
    );
  }

  const statValue = (render) => (summary ? render() : '—');
  const approved = Number(wallet.approvedBalance || 0);
  const pending = Number(wallet.unapprovedBalance || 0);

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      {/* Header: back, customer, top up */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Tooltip title="Back to wallets">
          <IconButton size="small" onClick={() => navigate('/wallets?tab=customers')}>
            <BackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '1rem', fontWeight: 600, lineHeight: 1.3 }} noWrap>{wallet.organizationName}</Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Customer wallet</Typography>
        </Box>
        {canWriteFin && (
          <Button variant="contained" size="small" startIcon={<TopUpIcon />} onClick={() => setTopUpOpen(true)}
            sx={{ ml: 'auto', textTransform: 'none', fontSize: '0.8125rem' }}>
            Top Up
          </Button>
        )}
      </Box>

      {topUpNotice && (
        <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setTopUpNotice(null)}>{topUpNotice}</Alert>
      )}

      <WalletTopUpDialog
        open={topUpOpen}
        walletId={id}
        organizationName={wallet.organizationName}
        onClose={() => setTopUpOpen(false)}
        onSubmitted={() => {
          setTopUpOpen(false);
          setTopUpNotice('Top-up submitted. It is pending until approved on Wallets → Top-ups.');
          fetchWallet();
          fetchSummary();
          fetchTransactions();
        }}
      />

      <ProTableStats
        cards={[
          { label: 'Available balance', value: WalletService.formatCurrency(approved), color: approved ? 'success.main' : 'text.disabled' },
          // Click: show only pending rows in the table below (click again to clear).
          { label: 'Pending top-ups', value: WalletService.formatCurrency(pending), color: pending ? 'warning.main' : 'text.disabled',
            highlight: statusFilter === 'PENDING',
            onClick: () => { setStatusFilter(statusFilter === 'PENDING' ? 'ALL' : 'PENDING'); setPage(0); } },
          { label: 'Total credits', value: statValue(() => WalletService.formatCurrency(summary.totalCredits)), color: summary?.totalCredits ? 'success.main' : 'text.disabled',
            highlight: tabValue === 1, onClick: () => handleTabChange(null, tabValue === 1 ? 0 : 1) },
          { label: 'Total debits', value: statValue(() => WalletService.formatCurrency(summary.totalDebits)), color: summary?.totalDebits ? 'error.main' : 'text.disabled',
            highlight: tabValue === 2, onClick: () => handleTabChange(null, tabValue === 2 ? 0 : 2) },
          { label: 'Transactions', value: statValue(() => summary.transactionCount) },
        ]}
      />

      {/* Toolbar */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5, ...TOOLBAR_SX }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={tabValue}
          onChange={(e, v) => { if (v !== null) handleTabChange(e, v); }}
          sx={{
            '& .MuiToggleButton-root': { height: 32, px: 1.5, fontSize: '0.75rem', textTransform: 'none' },
            '& .MuiToggleButton-root.Mui-selected': { bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' } },
          }}
        >
          <ToggleButton value={0}>All</ToggleButton>
          <ToggleButton value={1}>Credits</ToggleButton>
          <ToggleButton value={2}>Debits</ToggleButton>
        </ToggleButtonGroup>
        <TextField select size="small" value={typeFilter} onChange={handleTypeFilterChange} sx={{ minWidth: 170 }} label="Type">
          {TRANSACTION_TYPES.map((opt) => (
            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.8125rem' }}>{opt.label}</MenuItem>
          ))}
        </TextField>
        <TextField select size="small" value={statusFilter} onChange={handleStatusFilterChange} sx={{ minWidth: 150 }} label="Status">
          {TRANSACTION_STATUSES.map((opt) => (
            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.8125rem' }}>{opt.label}</MenuItem>
          ))}
        </TextField>
        <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap', mr: 1 }}>
            {totalTransactions} transactions
          </Typography>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => { fetchWallet(); fetchSummary(); fetchTransactions(); }}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {txError && <Alert severity="error" sx={{ mb: 1.5 }}>{txError}</Alert>}

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead sx={getBrandedTableHeadSx(theme)}>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Balance After</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {txLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell>
                </TableRow>
              ) : transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No transactions found</TableCell>
                </TableRow>
              ) : (
                transactions.map((tx) => {
                  const credit = tx.direction === 'CREDIT';
                  return (
                    <TableRow key={tx.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(tx.createdAt)}</TableCell>
                      <TableCell>
                        <Chip label={WalletService.getTransactionTypeLabel(tx.type)} size="small"
                          color={WalletService.getTransactionTypeColor(tx.type)} sx={{ height: 20, fontSize: '0.6875rem' }} />
                      </TableCell>
                      <TableCell>
                        <Chip label={WalletService.getTransactionStatusLabel(tx.status)} size="small"
                          color={WalletService.getTransactionStatusColor(tx.status)} sx={{ height: 20, fontSize: '0.6875rem' }} />
                      </TableCell>
                      <TableCell>{tx.description || '—'}</TableCell>
                      <TableCell>{tx.externalReference || tx.referenceId || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: credit ? 'success.main' : 'error.main', whiteSpace: 'nowrap' }}>
                        {credit ? '+' : '−'}{WalletService.formatCurrency(tx.amount)}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {/* null on PENDING rows — never render as AED 0.00 */}
                        {WalletService.formatCurrencyOrDash(tx.balanceAfter)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalTransactions}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>
    </Box>
  );
};

export default WalletDetail;
